<script setup>
import { ref, onMounted, computed } from 'vue'
import axios from '@/plugins/axios'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const router = useRouter()

const promotions = ref([])
const loading = ref(true)
const error = ref(false)

const BASE_URL = process.env.VUE_APP_BACKEND_URL

/* =========================
 * FETCH
 * ========================= */
const fetchPromotions = async () => {
  try {
    const res = await axios.get('/promotions')
    promotions.value = res.data.data || []
  } catch {
    error.value = true
  } finally {
    loading.value = false
  }
}

onMounted(fetchPromotions)

/* =========================
 * HELPERS
 * ========================= */
const hasTags = p =>
  p.percentage ||
  p.freeSpins ||
  p.maxBonusAmount

const goToDetail = promo => {
  router.push(`/promotions/${promo.code}`)
}
</script>

<template>
<section class="promo-page">

  <!-- HEADER -->
  <header class="promo-header">
    <h1>{{ t('promo.title') }}</h1>
    <p>{{ t('promo.subtitle') }}</p>
  </header>

  <!-- STATES -->
  <div v-if="loading" class="state">
    {{ t('common.loading') }}...
  </div>

  <div v-else-if="error" class="state error">
    {{ t('common.errorOccured') }}
  </div>

  <!-- GRID -->
  <div v-else class="promo-grid">
    <article
      v-for="p in promotions"
      :key="p.code"
      class="promo-card"
      @click="goToDetail(p)"
    >

      <!-- IMAGE -->
      <div
        v-if="p.img"
        class="promo-img"
        :style="{ backgroundImage: `url(${BASE_URL}${p.img})` }"
      />

      <!-- CONTENT -->
      <div class="promo-content">
        <h3>{{ t(p.title) }}</h3>

        <p v-if="p.shortDescription">
          {{ t(p.shortDescription) }}
        </p>

        <!-- TAGS -->
        <div v-if="hasTags(p)" class="promo-tags">

          <span v-if="p.percentage">
            {{ t('promo.percentage') }}: {{ p.percentage }}%
          </span>

          <span v-if="p.freeSpins">
            {{ t('promo.freeSpins') }}: {{ p.freeSpins }}
          </span>

          <span v-if="p.maxBonusAmount">
            {{ t('promo.maxBonus') }}: {{ p.maxBonusAmount }}
          </span>

        </div>

        <!-- CTA -->
        <button class="promo-btn">
          {{ t('promo.viewDetails') }}
        </button>
      </div>

    </article>
  </div>

</section>
</template>

<style scoped>
.promo-page {
  padding: 40px 24px;
  
}

.promo-header {
  text-align: center;
  margin-bottom: 40px;
}

.promo-header h1 {
  font-size: 28px;
  font-weight: 800;
}

.promo-header p {
  color: var(--subtext);
  margin-top: 6px;
}

.promo-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 28px;
  align-items: stretch; /* 🔥 hepsini aynı boy yapar */
}


.promo-card {
  background: var(--background2);
  border-radius: 18px;
  overflow: hidden;
  cursor: pointer;

  display: flex;
  flex-direction: column;
  height: 100%;

  transition: transform .25s ease, box-shadow .25s ease;
  box-shadow: 0 10px 30px rgba(0,0,0,.45);
}

 

.promo-img {
  height: 200px;
  background-size: cover;
  background-position: center;
}

.promo-content {
  padding: 18px;
  text-align: center;

  display: flex;
  flex-direction: column;
  flex: 1;
}

.promo-content h3 {
  font-size: 16px;
  font-weight: 800;
}

.promo-content p {
  font-size: 13px;
  color: var(--subtext);
  margin: 6px 0 10px;
}

.promo-tags {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 12px;
}

.promo-tags span {
  background: var(--background);
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
}

.promo-btn {
  margin-top: auto;
  background: var(--buttonyellow);
  box-shadow: var(--buttonyellowbox);
  border-radius: 12px;
  padding: 8px 16px;
  border: none;
  font-weight: 800;
  cursor: pointer;
}

</style>
